// Tabbed Reports Directory. Top-level tabs (Sales / Care Team /
// Customers / Accounting) filter a clean, tap-friendly list of reports.
// Selection is reflected in the URL (?tab=...) so managers can bookmark
// or share the exact view. Each tile deep-links to a dedicated report
// page that carries its own "Back to Reports" button.
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { subscribe as liveSubscribe, ensureOpen as liveEnsureOpen } from "@/lib/liveSync";
import { useAuth } from "@/lib/auth";

// Directory catalog. Every tile is defined once here so the tab body is
// a pure map, and adding a report later means editing this one array.
// Minimalist rev (iter-204): each entry only needs `tab`, `key`, `label`.
// Icons + descriptions were intentionally removed per operator feedback.
const REPORTS = [
  // ---- Sales -----------------------------------------------------------
  { tab: "sales", key: "daily-deposits", label: "Daily Deposits" },
  { tab: "sales", key: "orders", label: "Order Reports" },
  { tab: "sales", key: "orders-legacy", label: "Order Legacy" },
  { tab: "sales", key: "override-reasons", label: "Override Reasons" },
  { tab: "sales", key: "refunds", label: "Refunds by Day" },
  { tab: "sales", key: "unpaid-transactions", label: "Unpaid Transactions" },
  { tab: "sales", key: "finance-summary", label: "Finance Summary" },

  // ---- Care Team -------------------------------------------------------
  { tab: "care-team", key: "care-team-sales", label: "Care Team Sales" },
  { tab: "care-team", key: "care-team-productivity", label: "Care Team Productivity" },

  // ---- Customers -------------------------------------------------------
  { tab: "customers", key: "cancellations", label: "Cancellation Report" },
  { tab: "customers", key: "customers-by-day", label: "Customers by Day" },
  { tab: "customers", key: "top-spenders", label: "Top 15 High Spenders" },
  { tab: "customers", key: "future-bookings", label: "Future Booking" },
  { tab: "customers", key: "referrals", label: "Referral Report" },

  // ---- Accounting ------------------------------------------------------
  { tab: "accounting", key: "overview", label: "Location Overview" },
  { tab: "accounting", key: "email-log", label: "Email Delivery Log" },
];

const TAB_ORDER = [
  { key: "sales",       label: "Sales" },
  { key: "care-team",   label: "Care Team" },
  { key: "customers",   label: "Customers" },
  { key: "accounting",  label: "Accounting" },
];

export default function BranchReports() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TAB_ORDER.find((t) => t.key === searchParams.get("tab"))?.key || "sales";
  const [tab, setTab] = useState(initialTab);
  // Per-user tick-box gate (iter-205). Null = "still loading, show
  // everything" so nobody sees a blank list mid-fetch. Once resolved,
  // we filter the catalog through `allowedKeys` on every render.
  const [allowedKeys, setAllowedKeys] = useState(null);

  useEffect(() => {
    staffApi.get("/branches").then((r) => {
      setBranch(r.data.find((b) => b.id === bid) || null);
    }).catch((err) => toast.error(formatApiError(err)));
  }, [bid]);

  // Re-fetch on every mount so the directory re-evaluates individual
  // ticks live — matches the spec: "Reports page re-evaluates these
  // individual ticks on every load." Iter-239: also re-fetches on the
  // `report_permissions_updated` live-sync event / window fallback so a
  // manager toggling the current user's perms lights the tiles up
  // instantly without a manual refresh.
  const { staff } = useAuth();
  const refreshAllowed = useCallback(() => (
    staffApi
      .get("/report-permissions/me", { params: { scope: "branch" } })
      .then((r) => setAllowedKeys(new Set(r.data.allowed_keys || [])))
      .catch(() => setAllowedKeys(null))
  ), []);
  useEffect(() => { refreshAllowed(); }, [refreshAllowed]);
  useEffect(() => {
    if (!staff?.id) return undefined;
    liveEnsureOpen();
    const off = liveSubscribe("report_permissions_updated", (payload) => {
      if (payload && payload.user_id === staff.id) refreshAllowed();
    });
    const onLocal = (ev) => {
      const uid = ev?.detail?.user_id;
      if (uid && uid === staff.id) refreshAllowed();
    };
    window.addEventListener("cocoon:report-permissions-updated", onLocal);
    return () => {
      try { off && off(); } catch { /* noop */ }
      window.removeEventListener("cocoon:report-permissions-updated", onLocal);
    };
  }, [staff?.id, refreshAllowed]);

  const onTabChange = (v) => {
    setTab(v);
    // Keep URL shareable but avoid stacking history entries per tap.
    const params = new URLSearchParams(searchParams);
    params.set("tab", v);
    setSearchParams(params, { replace: true });
  };

  return (
    <StaffShell branchName={branch?.name} context="branch">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Location Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tap a report to open it — every page has a <span className="font-medium">Back to Reports</span> button so you can hop between them fast.
        </p>
      </div>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList className="mb-4 flex-wrap h-auto">
          {TAB_ORDER.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`reports-tab-${t.key}`}
              className="px-4 py-2"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TAB_ORDER.map((t) => {
          const catalogItems = REPORTS.filter((r) => r.tab === t.key);
          // Once the caller's perms have loaded, hide every tile that
          // isn't in their allow-list. While loading (null) we keep
          // showing the full catalog so the page never flashes empty.
          const items = allowedKeys ? catalogItems.filter((r) => allowedKeys.has(r.key)) : catalogItems;
          return (
            <TabsContent key={t.key} value={t.key} className="mt-0">
              {items.length === 0 ? (
                <p
                  className="text-xs text-muted-foreground py-3"
                  data-testid={`reports-empty-${t.key}`}
                >
                  No {t.label.toLowerCase()} reports yet.
                </p>
              ) : (
                <ul
                  className="flex flex-col divide-y divide-border/60 border-y border-border/60"
                  data-testid={`reports-list-${t.key}`}
                >
                  {items.map((r) => (
                    <li key={r.key}>
                      {/* Minimalist row — no card, no icon, no chevron.
                          The <Link> spans full width so the entire row
                          stays a single tap target; py-2.5 gives the
                          minimum vertical breathing room for a 44 × 44
                          Apple-HIG-friendly hit area without adding
                          visible spacing. */}
                      <Link
                        to={`/staff/branch/${bid}/reports/${r.key}`}
                        data-testid={`report-link-${r.key}`}
                        className="block w-full py-2.5 text-xs text-foreground hover:text-primary focus-visible:outline-none focus-visible:text-primary transition-colors"
                      >
                        {r.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </StaffShell>
  );
}
